import { useEffect, useState, useCallback, useMemo } from "react";
import HomeView from "./view";
import { useDispatch, useSelector } from "react-redux";
import { ReducersTypes } from "../../redux/reducers/Models";
import { debtsPulling, requestDebts, setUser, clearUser, authorize } from "../../redux/ducks/user";
import { clearCustomer, setCustomer } from "../../redux/ducks/customer";
import { PropsView } from "./Models";
import { Debts, getFormattedDebts } from "@/utils/transform";
import { getIsValidString } from "@/utils/functions";
import useRedirect from "../../hooks/useRedirect";
import { useNavigate } from "react-router-dom";
import { useSingleFlag } from "@/hooks/useFlags";
import useCaptcha from "@/hooks/useCaptcha";
import useCaptchaEnterprise from "@/hooks/useCaptchaEnterprise";

export default function Home() {
  useRedirect("home");

  const navigate = useNavigate();
  let TIMEOUT = 2000;
  const dispatch = useDispatch();
  const user = useSelector((state: ReducersTypes & {
    userReducer: ReducersTypes['userReducer'] & {
      cart: PropsView['selectables'];
      debts: PropsView['items'];
    }
  }) => state.userReducer);

  const { isEnabled: isCaptchaMtcCaptcha } = useSingleFlag('PD_CAPTCHA_MTCAPTCHA');
  const [isLoading, setIsLoading] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  
  const captchaName = useMemo(() => `mtcaptcha-home-modal-${captchaKey}`, [captchaKey]);
  const recaptchaName = useMemo(() => `home-modal-captcha-${captchaKey}`, [captchaKey]);

  const setIsCaptchaVerifiedCallback = useCallback((value: boolean) => {
    setIsCaptchaVerified(value);
  }, []);

  useCaptcha({
    setIsVerified: isCaptchaMtcCaptcha ? setIsCaptchaVerifiedCallback : () => {},
    captchaName
  });

  useCaptchaEnterprise({
    setIsVerified: !isCaptchaMtcCaptcha ? setIsCaptchaVerifiedCallback : () => {},
    captchaName: recaptchaName,
    action: "search"
  });
  
  const goBack = useCallback(() => navigate(-1), [navigate]);
  const goGetout = useCallback(() => {
    dispatch(clearCustomer());
    dispatch(clearUser());
    navigate('/');
  }, [dispatch, navigate]);
  const goToVehicles = useCallback(() => navigate('/vehicles'), [navigate]);
  const goToProfile = useCallback(() => navigate("/profile"), [navigate]);

  const resetCaptcha = useCallback(() => {
    setIsCaptchaVerified(false);
    
    if (isCaptchaMtcCaptcha) {
      if ((window as any)?.mtcaptcha?.resetUI) {
        (window as any).mtcaptcha.resetUI(captchaName);
      }
    } else {
      if ((window as any).grecaptcha?.enterprise?.reset) {
        (window as any).grecaptcha.enterprise.reset();
      } else if ((window as any).grecaptcha?.reset) {
        (window as any).grecaptcha.reset();
      }
      (window as any).recaptchaToken = null;
    }
  }, [isCaptchaMtcCaptcha, captchaName]);

  const incrementCaptchaKey = useCallback(() => {
    setCaptchaKey(prev => prev + 1);
  }, []);

  const handleDebtsPulling = useCallback((success: boolean, code?: string | Debts[]) => {
    if (!success) {
      if (code === "again") {
        setTimeout(() => {
          TIMEOUT = TIMEOUT*2;
          startPulling();
        }, TIMEOUT);
        return;
      }
      setIsLoading(false);
      dispatch(
        setUser({
          debts: [],
          cart: undefined,
        })
      );
      return;
    }

    const formattedDebts = getFormattedDebts(code as Debts[]);
    
    dispatch(
      setUser({
        debts: formattedDebts,
        cart: formattedDebts.map(item => ({ name: item.id, checked: true })),
      })
    );
    setIsLoading(false);
  }, [dispatch]);

  const startPulling = useCallback((requestId?: string) => {
    const currentRequestId = getIsValidString(requestId as string) ? requestId as string : user?.requestId as string;
    
    if (!getIsValidString(currentRequestId)) {
      return;
    }
    
    if (!isLoading) setIsLoading(true);

    dispatch(
      debtsPulling({
        requestId: currentRequestId,
        callback: handleDebtsPulling,
      })
    );
  }, [user?.requestId, isLoading, dispatch, handleDebtsPulling]);

  const onSearchNewPlate = useCallback(async (newPlate: string) => {
    const cleanPlate = newPlate.toUpperCase().replace(/[^A-Z0-9]/g, "");
    
    if (!cleanPlate || cleanPlate.length < 5) {
      return;
    }

    dispatch(
      setUser({
        plate: cleanPlate,
        requestId: undefined,
        debts: [],
        cart: undefined,
      })
    );

    dispatch(
      setCustomer({
        plate: cleanPlate,
      })
    );
    
    setIsLoading(true);

    try {
      let captchaToken = '';
      let recaptchaAction: string | undefined;

      if (isCaptchaMtcCaptcha) {
        const mtcaptchaToken = (window as any)?.mtcaptcha?.getVerifiedToken?.(captchaName);
        const mtcaptchaStatus = (window as any)?.mtcaptcha?.getStatus?.(captchaName);
        
        if (mtcaptchaToken && mtcaptchaStatus?.isVerified) {
          captchaToken = mtcaptchaToken;
        } else {
          if ((window as any)?.mtcaptcha?.getVerifiedToken) {
            const anyToken = (window as any).mtcaptcha.getVerifiedToken(captchaName);
            if (anyToken) {
              captchaToken = anyToken;
            }
          }
          
          if (!captchaToken) {
            captchaToken = (window as any)?.recaptchaToken || '';
          }
        }
      } else {
        let recaptchaToken = (window as any)?.recaptchaToken;
        
        if (!recaptchaToken && (window as any).executeRecaptcha) {
          (window as any).executeRecaptcha();
          await new Promise(resolve => setTimeout(resolve, 300));
          recaptchaToken = (window as any)?.recaptchaToken;
        }
        
        if (recaptchaToken) {
          captchaToken = recaptchaToken;
        }
        
        recaptchaAction = (window as any).getRecaptchaAction ? (window as any).getRecaptchaAction() : undefined;
      }

      if (!captchaToken) {
        await new Promise(resolve => setTimeout(resolve, 200));
        
        if (isCaptchaMtcCaptcha) {
          captchaToken = (window as any)?.mtcaptcha?.getVerifiedToken?.(captchaName) || '';
        } else {
          captchaToken = (window as any)?.recaptchaToken || '';
        }
      }

      dispatch(
        authorize({
          plate: cleanPlate,
          token: captchaToken,
          callback: (success: boolean) => {
            if (!success) {
              setIsLoading(false);
              dispatch(
                setUser({
                  debts: [],
                  cart: undefined,
                })
              );
              return;
            }

            setTimeout(() => {
              dispatch(
                requestDebts({
                  plate: cleanPlate,
                  callback: (success: boolean, code?: string) => {
                  if (!success) {
                    setIsLoading(false);
                    dispatch(
                      setUser({
                        debts: [],
                        cart: undefined,
                      })
                    );
                    return;
                  }

                  if (getIsValidString(code as string)) {
                    dispatch(
                      setUser({
                        requestId: code,
                      })
                    );
                    
                    setIsLoading(true);
                    dispatch(
                      debtsPulling({
                        requestId: code as string,
                        callback: handleDebtsPulling,
                      })
                    );
                  }
                },
              })
            );
            }, 100);
          },
          recaptchaAction,
        })
      );
    } catch (error) {
      setIsLoading(false);
    }
  }, [dispatch, isCaptchaMtcCaptcha, captchaName, handleDebtsPulling]);

  const onChange: PropsView['onChange'] = useCallback((e) => {
    const { name, checked } = e.target;

    if (name === "all") {
      dispatch(
        setUser({
          cart: checked ? user?.debts?.map(item => ({ name: item.id, checked: true })) : null,
        })
      );
      return;
    }

    const updatedSelectables = user?.cart?.filter(item => item.name !== name) || [];

    if (checked) {
      updatedSelectables.push({ name, checked });
    }

    dispatch(
      setUser({
        cart: updatedSelectables.length ? updatedSelectables : null
      })
    );
  }, [dispatch, user?.debts, user?.cart]);

  const handleRequestDebts = useCallback((success: boolean, code?: string) => {
    if (!success) {
      setIsLoading(false);
      dispatch(
        setUser({
          debts: [],
        })
      );
      return;
    }

    dispatch(
      setUser({
        requestId: code,
      })
    );
  }, [dispatch]);

  const getDebts = useCallback(() => {
    if (isLoading) return;
    if (getIsValidString(user?.requestId as string)) return;

    setIsLoading(true);
    dispatch(
      requestDebts({
        plate: user?.plate as string,
        callback: handleRequestDebts,
      })
    );
  }, [isLoading, user?.requestId, user?.plate, dispatch, handleRequestDebts]);

  useEffect(() => {
    if (getIsValidString(user?.requestId as string) && isLoading) {
      startPulling(user?.requestId as string);
    }
  }, [user?.requestId, isLoading, startPulling]);

  useEffect(() => {
    getDebts();
    if (getIsValidString(user?.requestId as string) && !isLoading) {
      startPulling(user?.requestId as string);
    }
  }, []);
  
  return (
    <HomeView 
      goToVehicles={goToVehicles}
      items={user?.debts || []}
      plate={user?.plate || ""}
      isLoading={isLoading}
      onChange={onChange}
      selectables={user?.cart || null}
      goBack={goBack}
      goToProfile={goToProfile}
      goGetout={goGetout}
      onSearchNewPlate={onSearchNewPlate}
      isCaptchaVerified={isCaptchaVerified}
      isCaptchaMtcCaptcha={isCaptchaMtcCaptcha}
      captchaName={captchaName}
      resetCaptcha={resetCaptcha}
      incrementCaptchaKey={incrementCaptchaKey}
    />
  );
}
